import { QueryClientProvider } from '@tanstack/react-query';
import { Link, Outlet, createRootRoute } from '@tanstack/react-router';
import { ThemeProvider } from 'next-themes';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Toaster } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { useApiError } from '@/hooks/use-api-error';
import { useLocale } from '@/hooks/use-locale';
import { captureError } from '@/lib/monitoring';
import { capturePlatformLock } from '@/lib/navigation';
import { queryClient } from '@/lib/query-client';

function RootLayout() {
  const { locale, direction } = useLocale();

  // Keeps <html lang>/<html dir> in step with the active locale for the whole
  // session, including after a hot reload.
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = direction;
  }, [locale, direction]);

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem
      storageKey="app_theme"
      disableTransitionOnChange
    >
      <QueryClientProvider client={queryClient}>
        <Outlet />
        <Toaster position={direction === 'rtl' ? 'top-left' : 'top-right'} dir={direction} />
      </QueryClientProvider>
    </ThemeProvider>
  );
}

/**
 * The last stop for an error that escaped a route. It reports the failure and
 * shows the user a translated message with the reference id, rather than a
 * blank screen.
 */
function RouteErrorBoundary({ error }: { error: unknown }) {
  const { t } = useTranslation('common');
  const toMessage = useApiError();
  const failure = toMessage(error);

  useEffect(() => {
    captureError(error, { boundary: 'root' });
  }, [error]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="text-lg font-medium">{failure.message}</p>
      <Button onClick={() => globalThis.location.reload()}>{t('actions.retry')}</Button>
    </div>
  );
}

/**
 * A route the router itself could not match (rule E4, case b) — distinct from
 * an API 404 for a record that once could have existed (case a, handled by
 * `useApiError()` at the point of the failed request). No request was made
 * here, so there is nothing to correlate (no reference id) and no retry that
 * would behave differently; the one useful recovery is a link back to a page
 * that exists. Matches `RouteErrorBoundary`'s standard: translated copy, no
 * raw path or router internals shown.
 */
function NotFound() {
  const { t } = useTranslation('common');

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="text-lg font-medium">{t('notFound.title')}</p>
      <p className="text-sm text-muted-foreground">{t('notFound.description')}</p>
      <Button asChild>
        <Link to="/">{t('notFound.action')}</Link>
      </Button>
    </div>
  );
}

export const Route = createRootRoute({
  // unified-access.md section 3.2: `lock=1` next to a platform's absolute
  // `returnTo`, on any page, starts single-platform sign-in for this tab.
  beforeLoad: ({ location }) => {
    capturePlatformLock(location.search as Record<string, unknown>);
  },
  component: RootLayout,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: NotFound,
});
